import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

interface FooterActionsContextValue {
    actions: ReactNode;
    setActions: (actions: ReactNode) => void;
    clearActions: () => void;
}

const FooterActionsContext = createContext<FooterActionsContextValue>({
    actions: null,
    setActions: () => undefined,
    clearActions: () => undefined,
});

interface FooterActionsProviderProps {
    children: ReactNode;
}

/**
 * Lets a deeply-nested page push action buttons up into the footer's "with nav"
 * band, even though `PageLayout` (which renders the `Footer`) is an ancestor of
 * the page rather than a child. Mirrors `HeaderActionsProvider`.
 *
 * A page opts in by calling `setActions(...)` (typically in an effect, returning
 * `clearActions` as cleanup); pages that never set actions render the plain
 * copyright footer.
 */
export const FooterActionsProvider: React.FC<FooterActionsProviderProps> = ({ children }) => {
    const [actions, setActions] = useState<ReactNode>(null);

    const clearActions = useCallback(() => setActions(null), []);

    const value = useMemo(
        () => ({
            actions,
            setActions,
            clearActions,
        }),
        [actions, clearActions]
    );

    return <FooterActionsContext.Provider value={value}>{children}</FooterActionsContext.Provider>;
};

export const useFooterActions = (): FooterActionsContextValue => useContext(FooterActionsContext);
